/**
 * BlobStorageTab Component
 * Admin tab for configuring tenant blob storage (SROP-managed vs self-managed)
 */

import { useState } from 'react';
import {
  Loader2,
  Check,
  AlertTriangle,
  RefreshCw,
  Database,
  Cloud,
  Server,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  useTenantBlobStorage,
  useUpdateBlobStorage,
  useValidateBlobStorage,
  useProvisionBlobStorage,
} from '../../hooks/useBlobStorage';

// Storage type options
const STORAGE_TYPES = [
  {
    value: 'srop_managed',
    label: 'SROP Managed',
    description: 'Platform-managed storage with automatic provisioning',
    icon: Cloud,
  },
  {
    value: 'self_managed',
    label: 'Self Managed',
    description: 'Your own Azure storage account (enterprise)',
    icon: Server,
  },
];

// Validation status badge component
function ValidationBadge({ status }) {
  const statusConfig = {
    connected: { bgColor: 'bg-green-100 dark:bg-green-900/30', textColor: 'text-green-800 dark:text-green-400', icon: Check, label: 'Connected' },
    partial: { bgColor: 'bg-yellow-100 dark:bg-yellow-900/30', textColor: 'text-yellow-800 dark:text-yellow-400', icon: AlertTriangle, label: 'Partial' },
    disconnected: { bgColor: 'bg-red-100 dark:bg-red-900/30', textColor: 'text-red-800 dark:text-red-400', icon: AlertTriangle, label: 'Disconnected' },
    pending: { bgColor: 'bg-gray-100 dark:bg-gray-700', textColor: 'text-gray-800 dark:text-gray-400', icon: Database, label: 'Pending' },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

export default function BlobStorageTab({ tenantId }) {
  const { data: storageData, isLoading } = useTenantBlobStorage(tenantId);
  const updateMutation = useUpdateBlobStorage(tenantId);
  const validateMutation = useValidateBlobStorage(tenantId);
  const provisionMutation = useProvisionBlobStorage(tenantId);

  const [isEditing, setIsEditing] = useState(false);
  const [showSasToken, setShowSasToken] = useState(false);
  const [formData, setFormData] = useState({
    type: 'srop_managed',
    storage_account_url: '',
    container_name: '',
    sas_token: '',
  });

  // Initialize form when data loads
  const config = storageData?.config || storageData || {};

  const handleEdit = () => {
    setFormData({
      type: config.type || 'srop_managed',
      storage_account_url: config.storage_account_url || '',
      container_name: config.container_name || '',
      sas_token: '', // Never pre-fill SAS token
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setShowSasToken(false);
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync(formData);
      setIsEditing(false);
      setShowSasToken(false);
    } catch (error) {
      console.error('Failed to update blob storage config:', error);
    }
  };

  const handleValidate = async () => {
    try {
      await validateMutation.mutateAsync();
    } catch (error) {
      console.error('Failed to validate blob storage:', error);
    }
  };

  const handleProvision = async () => {
    try {
      await provisionMutation.mutateAsync();
    } catch (error) {
      console.error('Failed to provision container:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Configuration Card */}
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white">Storage Configuration</h4>
          <ValidationBadge status={config.validation_status} />
        </div>

        {!isEditing ? (
          <div className="space-y-4">
            {/* Display Mode */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-400">Type</label>
                <p className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                  {(config.type || 'srop_managed').replace('_', ' ')}
                </p>
              </div>
              <div>
                <label className="text-xs text-gray-400">Container</label>
                <p className="text-sm font-mono text-gray-900 dark:text-white">
                  {config.container_name || 'Not configured'}
                </p>
              </div>
              {config.type === 'self_managed' && (
                <div className="col-span-2">
                  <label className="text-xs text-gray-400">Storage Account URL</label>
                  <p className="text-sm font-mono text-gray-900 dark:text-white break-all">
                    {config.storage_account_url || 'Not configured'}
                  </p>
                </div>
              )}
              {config.validated_at && (
                <div className="col-span-2">
                  <label className="text-xs text-gray-400">Last Validated</label>
                  <p className="text-sm text-gray-900 dark:text-white">
                    {new Date(config.validated_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleEdit}
                className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
              >
                Edit Configuration
              </button>
              <button
                onClick={handleValidate}
                disabled={validateMutation.isPending}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-700 dark:text-gray-400"
              >
                {validateMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Test Connection
              </button>
              {config.type === 'srop_managed' && config.validation_status === 'pending' && (
                <button
                  onClick={handleProvision}
                  disabled={provisionMutation.isPending}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-600 hover:text-green-700"
                >
                  {provisionMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Database className="w-4 h-4" />
                  )}
                  Provision Container
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Edit Mode */}
            {/* Storage Type Selection */}
            <div>
              <label className="block text-xs text-gray-400 mb-2">Storage Type</label>
              <div className="grid grid-cols-2 gap-3">
                {STORAGE_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = formData.type === type.value;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, type: type.value })}
                      className={`p-3 rounded-lg border-2 text-left transition-colors ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={`w-4 h-4 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className={`text-sm font-medium ${isSelected ? 'text-blue-600' : 'text-gray-900 dark:text-white'}`}>
                          {type.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">{type.description}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Container Name */}
            <div>
              <label className="block text-xs text-gray-400 mb-1">Container Name</label>
              <input
                type="text"
                value={formData.container_name}
                onChange={(e) => setFormData({ ...formData, container_name: e.target.value })}
                placeholder={`srop-staging-data-${tenantId}`}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              />
            </div>

            {/* Self-Managed Fields */}
            {formData.type === 'self_managed' && (
              <>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Storage Account URL</label>
                  <input
                    type="text"
                    value={formData.storage_account_url}
                    onChange={(e) => setFormData({ ...formData, storage_account_url: e.target.value })}
                    placeholder="https://yourstorage.blob.core.windows.net"
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">SAS Token (optional)</label>
                  <div className="relative">
                    <input
                      type={showSasToken ? 'text' : 'password'}
                      value={formData.sas_token}
                      onChange={(e) => setFormData({ ...formData, sas_token: e.target.value })}
                      placeholder="sv=2021-06-08&ss=b&srt=co&sp=r..."
                      className="w-full px-3 py-2 pr-10 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSasToken(!showSasToken)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showSasToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Leave blank to keep existing token</p>
                </div>
              </>
            )}

            {/* Edit Mode Buttons */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="inline-flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
              >
                {updateMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Save Configuration
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700 dark:text-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Validation Results */}
      {validateMutation.data && (
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Validation Results</h4>
          {validateMutation.data.paths_found?.length > 0 && (
            <div className="mb-3">
              <p className="text-xs text-gray-400 mb-1">Found Paths</p>
              <ul className="space-y-1">
                {validateMutation.data.paths_found.map((path) => (
                  <li key={path} className="text-xs font-mono text-green-600 dark:text-green-400 flex items-center gap-1">
                    <Check className="w-3 h-3" /> {path}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {validateMutation.data.paths_missing?.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 mb-1">Missing Paths</p>
              <ul className="space-y-1">
                {validateMutation.data.paths_missing.map((path) => (
                  <li key={path} className="text-xs font-mono text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {path}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {validateMutation.data.error && (
            <p className="text-xs text-red-600 dark:text-red-400">{validateMutation.data.error}</p>
          )}
        </div>
      )}
    </div>
  );
}
